import React, { useMemo, useCallback } from "react";
import {
	DarkBlackLabelLogoIcon,
	InstagramIcon,
	LinkedinIcon,
	LoginIcon,
	LogoLightFooterIcon,
	TiktokIcon,
} from "@assets/icons";
import P from "@components/common/P/P";
import SvgIcon from "@components/common/SvgIcon/SvgIcon";
import { useFetchContents } from "hooks/apiHooks/useFetchContents";
import { useTheme } from "hooks/useTheme";
import type { Section } from "types/content";
import LoadingSpinner from "@components/LoadingSpinner";
import ErrorCatching from "@components/ErrorCatching/ErrorCatching";
import MainAppQR from "@assets/images/main_app.webp";
import DriverAppQR from "@assets/images/driver_qr.webp";
import MainAppIOSQR from "@assets/images/main_app_ios.webp";
import DriverAppIOSQR from "@assets/images/driver_ios_qr.webp";

import { useTranslation } from "react-i18next";
import { useLang } from "shared/i18n/useLang";
import { langToContentLangId } from "@utils/langToContentLangId";
import { useSelector } from "react-redux";
import { RootState } from "store/store";
import { ROUTES } from "constants/routes";
import { useNavigate } from "react-router-dom";
import { useLandingSearchNav } from "hooks/useLandingSearchNav";
import { FaFacebook } from "react-icons/fa";

const PLAY_STORE_URL =
	"https://play.google.com/store/apps/details?id=com.tex.logistic&pcampaignid=web_share";

const PLAY_STORE_DRIVER_URL =
	"https://play.google.com/store/apps/details?id=org.texlogistic.client&pcampaignid=web_share";

// ✅ add your real iOS links here
const APP_STORE_URL = "https://apps.apple.com/app/tex-logistics/id6753087924";
// const APP_STORE_DRIVER_URL =
// 	"https://apps.apple.com/app/tex-logistics-driver/id6753087925";

interface FooterProps {
	onOpenLogin: () => void;
	onOpenRegister: () => void;
}

const Footer: React.FC<FooterProps> = ({ onOpenLogin, onOpenRegister }) => {
	const { theme } = useTheme();
	const { t } = useTranslation();
	const { lang } = useLang();
	const navigate = useNavigate();

	const { handleLandingSearchClick } = useLandingSearchNav(onOpenRegister);

	const isAuthenticated = useSelector(
		(state: RootState) => state.auth.isAuthenticated,
	);

	// ✅ backend language id (1=en, 2=ru, 3=tr)
	const langId = useMemo(() => langToContentLangId(lang), [lang]);

	// ✅ refetch contents when language changes
	const {
		data: allContentData,
		isLoading,
		error,
	} = useFetchContents("1", langId);

	console.log("=== RAW FETCH DATA ===");
	console.log(allContentData);

	const list = useMemo<Section[]>(() => {
		const arr = allContentData?.data;
		return Array.isArray(arr) ? (arr as Section[]) : [];
	}, [allContentData]);

	console.log("=== PARSED LIST ===");
	console.log(list);
	console.log(
		"Section names:",
		list.map((s) => s.name),
	);

	const getSectionById = useCallback(
		(id: number) => list.find((s) => s.id === id),
		[list],
	);

	const contactData = getSectionById(16);
	const socialLinksData = getSectionById(17);

	console.log("Contact section:", contactData);
	console.log("Social links section:", socialLinksData);

	const { phoneNumber, email } = useMemo(() => {
		const items = contactData?.contents ?? [];

		return {
			phoneNumber:
				items.find((x: any) => x.title === "phoneNumber")?.subtitle || "",
			email: items.find((x: any) => x.title === "email")?.subtitle || "",
		};
	}, [contactData]);
	console.log("Footer contact data", { phoneNumber, email });

	const socials = useMemo(() => {
		const socialItems = socialLinksData?.contents ?? [];
		const contactItems = contactData?.contents ?? [];

		const mergedItems = [...socialItems, ...contactItems];

		return mergedItems
			.map((item: any) => {
				const title = String(item.title || "")
					.toLowerCase()
					.trim();
				const href = String(item.subtitle || "").trim();

				if (!href) return null;

				if (title === "instagram") {
					return { id: `instagram-${item.id}`, href, Icon: InstagramIcon };
				}

				if (title === "linkedin") {
					return { id: `linkedin-${item.id}`, href, Icon: LinkedinIcon };
				}

				if (title === "tiktok") {
					return { id: `tiktok-${item.id}`, href, Icon: TiktokIcon };
				}

				// ✅ skype title can contain Facebook URL in subtitle
				if (title === "skype" && href.toLowerCase().includes("facebook.com")) {
					return { id: `skype-${item.id}`, href, Icon: FaFacebook };
				}

				return null;
			})
			.filter(Boolean) as Array<{ id: string; href: string; Icon: any }>;
	}, [socialLinksData, contactData]);

	const qrItems = useMemo(
		() => [
			{
				id: "android-main",
				href: PLAY_STORE_URL,
				img: MainAppQR,
				alt: t("landing.footer.qrAppAlt"),
				label: t("landing.footer.app"),
			},
			{
				id: "android-driver",
				href: PLAY_STORE_DRIVER_URL,
				img: DriverAppQR,
				alt: t("landing.footer.qrDriverAlt"),
				label: t("landing.footer.driver"),
			},
			{
				id: "ios-main",
				href: APP_STORE_URL,
				img: MainAppIOSQR,
				alt: t("landing.footer.qrAppIosAlt"),
				label: t("landing.footer.appIos"),
			},
			// {
			// 	id: "ios-driver",
			// 	href: APP_STORE_DRIVER_URL,
			// 	img: DriverAppIOSQR,
			// 	alt: t("landing.footer.qrDriverIosAlt"),
			// 	label: t("landing.footer.driverIos"),
			// },
		],
		[t],
	);

	if (isLoading) return <LoadingSpinner />;

	if (error) return <ErrorCatching message={t("landing.footer.error")} />;

	return (
		<footer className="grid grid-flow-row grid-cols-1 gap-6 lg:mt-10 bg-light-mainColor dark:bg-dark-mainColor auto-rows-max minPhone:p-6 lg:p-14 rounded-3xl">
			<div className="grid gap-4 pb-6 minPhone:grid-cols-1 lg:grid-cols-maxTwo place-content-between minPhone:place-items-start lg:items-start">
				<SvgIcon
					Icon={
						theme === "light" ? LogoLightFooterIcon : DarkBlackLabelLogoIcon
					}
					className="lg:w-[133px] lg:h-[70px] minPhone:w-[90px] minPhone:h-[60px]"
				/>

				<div className="grid grid-flow-row grid-cols-1 gap-4 auto-rows-max minPhone:place-items-start lg:place-items-end">
					<P
						size="p1"
						type="regular"
						className="text-light-whiteText dark:text-dark-whiteText"
					>
						{phoneNumber || ""}
					</P>

					<P
						size="p1"
						type="regular"
						className="text-light-whiteText dark:text-dark-whiteText"
					>
						{email || ""}
					</P>

					<div className="grid gap-6 grid-cols-maxFour place-items-center">
						{socials.map(({ id, href, Icon }) => (
							<a key={id} href={href} target="_blank" rel="noopener noreferrer">
								<SvgIcon
									Icon={Icon}
									className="fill-light-whiteText dark:fill-dark-whiteText w-6 h-6"
								/>
							</a>
						))}
					</div>

					<div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-4">
						{qrItems.map((item) => (
							<a
								key={item.id}
								href={item.href}
								target="_blank"
								rel="noopener noreferrer"
								className="flex flex-col items-center gap-1"
							>
								<img
									src={item.img}
									alt={item.alt}
									className="w-24 h-24 rounded-xl bg-white p-2 cursor-pointer"
									loading="lazy"
								/>
								<P
									size="t2"
									type="regular"
									className="text-light-whiteText dark:text-dark-whiteText text-center"
								>
									{item.label}
								</P>
							</a>
						))}
					</div>
				</div>
			</div>

			<div className="grid gap-4 py-2 pt-6 border-t minPhone:grid-cols-1 lg:grid-cols-maxTwo place-content-between minPhone:place-items-start lg:place-items-center border-light-strokeLanding dark:border-dark-strokeLanding">
				<div className="grid minPhone:gap-4 lg:gap-6 minPhone:grid-cols-1 lg:grid-cols-maxThree lg:place-items-center">
					<P
						size="t1"
						type="semibold"
						className="cursor-pointer text-light-whiteText dark:text-dark-whiteText"
						onClick={() => handleLandingSearchClick("cargo")}
					>
						{t("landing.nav.findCargo")}
					</P>

					<P
						size="t1"
						type="semibold"
						className="cursor-pointer text-light-whiteText dark:text-dark-whiteText"
						onClick={() => handleLandingSearchClick("transport")}
					>
						{t("landing.nav.findTransport")}
					</P>

					<P
						size="t1"
						type="semibold"
						className="cursor-pointer text-light-whiteText dark:text-dark-whiteText"
						onClick={() => navigate(ROUTES.TERMS)}
					>
						{t("landing.footer.terms")}
					</P>
				</div>

				<div className="grid gap-4 grid-cols-maxTwo place-items-center">
					{isAuthenticated ? (
						<div
							className="bg-light-bgColor dark:bg-dark-bgColor py-2.5 px-4 rounded-full cursor-pointer"
							onClick={() => navigate(ROUTES.MAIN)}
						>
							<P
								size="t1"
								type="semibold"
								className="text-light-mainColor dark:text-dark-mainColor"
							>
								{t("landing.nav.platform")}
							</P>
						</div>
					) : (
						<>
							<div
								className="grid gap-1 cursor-pointer grid-cols-maxTwo place-items-center"
								onClick={onOpenLogin}
							>
								<SvgIcon
									Icon={LoginIcon}
									className="stroke-light-whiteText dark:stroke-dark-whiteText"
								/>
								<P
									size="t1"
									type="semibold"
									className="text-light-whiteText dark:text-dark-whiteText"
								>
									{t("auth.login.login")}
								</P>
							</div>

							<div
								className="bg-light-bgColor dark:bg-dark-bgColor py-2.5 px-4 rounded-full cursor-pointer"
								onClick={onOpenRegister}
							>
								<P
									size="t1"
									type="semibold"
									className="text-light-mainColor dark:text-dark-mainColor"
								>
									{t("auth.register.register")}
								</P>
							</div>
						</>
					)}
				</div>
			</div>
		</footer>
	);
};

export default React.memo(Footer);
